import React, {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react"
import { FaSpinner } from "react-icons/fa"
import AutosizeInput from "react-input-autosize"
import { toast } from "react-toastify"

import Button from "@fuse/sharedComponents/buttons/SolidBtn"
import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import { TWebUser } from "@letshang/constants"
import moment from "moment"

import {
  capitalizeSentence,
  DobType,
  getDateFormatForCountry,
  getInputConfig,
  parseDateParts,
  validateDate,
} from "@fuse/pages/signUp/steps/Dob"

import IconCalendar from "@fuse/assets/images/Profile/img-icon-calendar.svg"

const AutosizeInputText = AutosizeInput as any

interface AskGenderPopupProps {
  isLoading?: boolean
  user?: TWebUser
  onUpdateUserDetails: (v: Record<string, string>) => void
  onCloseDetailRowPopup: () => void
}

const AskGenderPopup: React.FC<AskGenderPopupProps> = ({
  isLoading,
  user,
  onUpdateUserDetails,
  onCloseDetailRowPopup,
}) => {
  const [isValidDate, setIsValidDate] = useState(true)
  const [dateErrorText, setDateErrorText] = useState<string | null>(null)

  const dateFormat = getDateFormatForCountry(user?.country)
  const inputConfig = getInputConfig(dateFormat)

  const [dob, setDob] = useState<DobType>({
    day: "",
    month: "",
    year: "",
  })

  const refs = {
    day: useRef<HTMLInputElement>(null),
    month: useRef<HTMLInputElement>(null),
    year: useRef<HTMLInputElement>(null),
  }

  const getNextInput = (currentKey: string): keyof typeof refs | null => {
    const sortedInputs = Object.entries(inputConfig)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([key]) => key)

    const currentIndex = sortedInputs.indexOf(currentKey)
    return currentIndex < sortedInputs.length - 1
      ? (sortedInputs[currentIndex + 1] as keyof typeof refs)
      : null
  }

  const getPrevInput = (currentKey: string): keyof typeof refs | null => {
    const sortedInputs = Object.entries(inputConfig)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([key]) => key)

    const currentIndex = sortedInputs.indexOf(currentKey)
    return currentIndex > 0
      ? (sortedInputs[currentIndex - 1] as keyof typeof refs)
      : null
  }

  const handleKeyDown = (
    key: keyof DobType,
    e: KeyboardEvent<HTMLInputElement>,
  ) => {
    const nextInput = getNextInput(key)
    const prevInput = getPrevInput(key)
    const maxLength = inputConfig[key].maxLength

    if (e.currentTarget.value.length >= maxLength && e.key !== "Backspace") {
      nextInput && refs[nextInput].current?.focus()
    }
    if (e.key === "Backspace" && e.currentTarget.value === "") {
      prevInput && refs[prevInput].current?.focus()
    }
  }

  const handleInputChange = (
    key: keyof DobType,
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value
    const maxVal =
      key === "month" ? 12 : key === "day" ? 31 : new Date().getFullYear()

    if (Number(value) > maxVal) {
      e.target.value = maxVal.toString()
    }

    setDob((prev) => ({ ...prev, [key]: e.target.value }))
  }

  const handleNext = async () => {
    if (Number(dob.year) > new Date().getFullYear() - 18) {
      showErrorToast("Date format is invalid!")
      return
    }

    const dateString = parseDateParts(dob.day, dob.month, dob.year, dateFormat)
    if (!validateDate(dob.day, dob.month, dob.year, dateFormat)) {
      showErrorToast("Date format is invalid!")
      return
    }

    const dobInterpretationFormat = getDateFormatForCountry(
      user.country,
    ).replace("/", "-")
    const formattedDOB = moment(dateString, dobInterpretationFormat).format(
      "YYYY-MM-DD",
    )

    onUpdateUserDetails({
      dateOfBirth: formattedDOB,
    })
  }

  useEffect(() => {
    const validateAndFormatDate = (): string | null => {
      setDateErrorText("")
      setIsValidDate(true)
      const dateString = parseDateParts(
        dob.day,
        dob.month,
        dob.year,
        dateFormat,
      )
      if (dateString.length === 10) {
        const date = moment(dateString, dateFormat, true)

        // Check if the date is invalid
        if (!date.isValid()) {
          setIsValidDate(false)
          setDateErrorText("Date is not valid")
          return null
        }

        // Check if the date is in the future
        if (date.isAfter(moment())) {
          setIsValidDate(false)
          setDateErrorText("Future dates are not allowed")
          return null
        }

        // Check if the age is less than 18
        const age = moment().diff(date, "years")
        if (age < 18) {
          setIsValidDate(false)
          setDateErrorText("You must be at least 18 years old")
          return null
        }

        // Check if the age exceeds 80
        if (age > 80) {
          setIsValidDate(false)
          setDateErrorText("Age cannot exceed 80 years")
          return null
        }

        // If all validations pass
        setIsValidDate(true)
        setDateErrorText("")
        return date.format("MMMM DD, YYYY")
      }
    }

    // Perform validation and set formatted date
    validateAndFormatDate()
  }, [dob, dateFormat])

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen w-full"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onCloseDetailRowPopup()
        }
      }}
    >
      <div className="w-[90%] max-w-lg bg-black backdrop-blur-md rounded-2xl p-10">
        <div className="text-left">
          <h2 className="text-white text-2xl font-semibold">Date of birth</h2>

          <p className="text-white mt-2 text-sm">
            Tell us your birthday so we can personalize your experience with the
            app.
          </p>
        </div>

        <div className="flex flex-row mt-6">
          <div className="flex flex-row gap-3 blurTypeLight rounded-md px-2 py-2.5">
            <div className="flex items-center justify-center">
              <img src={IconCalendar} />
            </div>
            <div className="h-[90%] w-[0.5px] bg-white/35 self-center"></div>
            <div className="flex flex-col items-start mr-1">
              <p className="text-white/35 text-xs">DOB</p>
              <div className="flex items-center w-full justify-start mt-1">
                {Object.entries(inputConfig)
                  .sort(([, a], [, b]) => a.order - b.order)
                  .map(([key, config], index, array) => (
                    <div key={key} className="flex items-start flex-shrink-0">
                      <AutosizeInputText
                        inputRef={(input: any) => {
                          refs[key as keyof typeof refs].current = input
                        }}
                        name={key}
                        // type="number"
                        inputMode="numeric"
                        placeholder={capitalizeSentence(config.placeholder)}
                        value={dob[key as keyof DobType]}
                        maxLength={config.maxLength}
                        onChange={(e: any) =>
                          handleInputChange(key as keyof DobType, e)
                        }
                        className="h-full text-sm text-black placeholder-white/35 outline-none text-left w-auto max-w-none flex-shrink-0 placeholder:capitalize"
                        onKeyDown={(e: any) =>
                          handleKeyDown(key as keyof DobType, e)
                        }
                        inputStyle={styles}
                      />
                      {index < array.length - 1 && (
                        <span className="text-[15px] text-white opacity-[0.3]">
                          /
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
        {!isValidDate && (
          <div className="mt-2">
            <p className={`text-[#f87171] text-start`}>{dateErrorText}</p>
          </div>
        )}

        <div className="flex pt-14">
          <Button
            className="w-full bg-white/20 text-white py-3 rounded-full font-medium transition"
            onClick={handleNext}
            disabled={
              dob.day.length < 2 ||
              dob.month.length < 2 ||
              dob.year.length < 4 ||
              !isValidDate
            }
          >
            {isLoading ? (
              <FaSpinner className="animate-spin mr-2" size={22} />
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AskGenderPopup

const styles = {
  color: "white",
  backgroundColor: "transparent",
  outline: "none",
  boxShadow: "none",
}
